/**
 * The scan order: which target the highlight visits, and in what sequence (3.3).
 *
 * Pure — geometry in, ordered rows out — because this is where C59 is resolved and a resolution nobody can
 * test is a resolution nobody should trust.
 *
 * ── Visual order is derived, not declared ──
 *
 * The phase gotcha is blunt: "scan order must match visual order. A scan that jumps around is unusable
 * regardless of interval. Derive it from DOM order and assert it in a test."
 *
 * So the order **is** the geometry. Targets are grouped into rows by where they actually are on screen and
 * sorted left to right within each row. No surface declares anything, which means no surface can forget to —
 * and a layout change cannot leave a stale ordering behind, because there is no ordering to leave behind.
 *
 * ── C59: the two requirements 3.3 states cannot both hold ──
 *
 * "Scanning order follows visual order for every surface, including the persistent bar" and "quick fires and
 * STOP reachable early in the scan cycle — not after the whole keyboard". The bar is at the *bottom* of the
 * screen. Taken literally, reaching STOP means scanning the entire keyboard first, which is precisely the
 * failure the second requirement names.
 *
 * Resolved by ordering the cycle bottom-up rather than top-down, and by hoisting one target:
 *
 *   1. **STOP alone**, as its own row. One item, so selecting the row activates it — about one interval
 *      instead of five. Non-negotiable §3.3 says STOP must always work, and scanning is the mode in which the
 *      person is slowest; this is the one place worth deviating, and it deviates *toward* 3.3's second
 *      requirement rather than splitting the difference.
 *   2. The persistent bar, its own rows in visual order: quick fires, then the action row.
 *   3. Everything else, in literal visual order.
 *
 * Consistent and learnable, which is what the gotcha actually protects. What it forbids is a scan that jumps
 * about — not one that starts at the bottom.
 */

export interface ScanTarget {
  id: string;
  /** Screen position. Only `top` and `left` are used, and only for ordering. */
  top: number;
  left: number;
  /** Used to decide what counts as "the same row". */
  height: number;
}

export type ScanRow = readonly ScanTarget[];

/** The one target hoisted to the front of every cycle. */
export const STOP_TARGET_ID = 'action:stop';

/**
 * Fraction of a target's height within which two targets count as the same row.
 *
 * Half a row: generous enough that a target sitting a couple of pixels high because of a border still groups
 * with its neighbours, tight enough that two genuine rows never merge. Rows in this app are separated by at
 * least a key gap, so there is no near-miss case to get wrong.
 */
const ROW_TOLERANCE = 0.5;

/**
 * Group targets into scan rows.
 *
 * Empty in, empty out — a surface with nothing to activate is not an error, it is a surface mid-render.
 */
export function scanRows(targets: readonly ScanTarget[]): ScanRow[] {
  if (targets.length === 0) return [];

  const stop = targets.filter((target) => target.id === STOP_TARGET_ID);
  const rest = targets.filter((target) => target.id !== STOP_TARGET_ID);

  // Bottom-up, so the persistent bar is reached before the keyboard (C59). Within a row, left to right, which
  // is the reading order the person already uses for the keys.
  const sorted = [...rest].sort((a, b) => b.top - a.top || a.left - b.left);

  const rows: ScanTarget[][] = [];
  for (const target of sorted) {
    const current = rows.at(-1);
    const anchor = current?.[0];

    if (anchor && Math.abs(anchor.top - target.top) <= anchor.height * ROW_TOLERANCE) {
      current.push(target);
      continue;
    }
    rows.push([target]);
  }

  for (const row of rows) row.sort((a, b) => a.left - b.left);

  // STOP first, as its own row, and only if it is actually on screen — it lives in the persistent bar, which
  // Betreuer-Modus and Partner-Ansicht replace entirely.
  return stop.length > 0 ? [stop, ...rows] : rows;
}

/**
 * Read the current targets out of a container.
 *
 * **This sees exactly what carries `data-activate-id`, and nothing else — which is not the same thing as every
 * target** (ADR-046). This comment used to say the query was complete "because every interactive thing in this
 * app is an `ActivateButton`". `CON-01` requires that; the code did not do it, and stating it as settled fact
 * here is why nobody checked. The repair control was outside the registry for a release, so the scan could not
 * reach the one control that gets the person's voice back, and every test still passed. Eleven other plain
 * buttons are still outside it — `CON-01`'s known limit names them.
 *
 * The unit test only asserts the other direction: nothing carrying `data-activate-id` may be missing from the
 * order. It cannot see a control that never had the attribute.
 */
export function readScanTargets(root: ParentNode): ScanTarget[] {
  const out: ScanTarget[] = [];

  for (const element of root.querySelectorAll<HTMLElement>('[data-activate-id]')) {
    const id = element.dataset.activateId;
    if (id === undefined) continue;

    const rect = element.getBoundingClientRect();
    // Zero-sized means not laid out or hidden. Scanning to something invisible would be a dead step the
    // person cannot understand — the highlight would simply vanish for one interval.
    if (rect.width === 0 || rect.height === 0) continue;

    out.push({ id, top: rect.top, left: rect.left, height: rect.height });
  }

  return out;
}
